import React from "react";
import { FormatterProps } from "react-data-grid";

interface MapWithName {
  name: string;
}

function valueMapMapper<T extends MapWithName>(
  key: string,
  map: Map<identifier, T>,
  multiple?: boolean,
  format?: (mapped?: T) => string,
  fallbackFn?: (row: any) => string | undefined
) {
  const _format = format ? format : (mapped?: T) => mapped?.name || "";

  return React.memo((props: FormatterProps) => {
    if (!props.row[key] || props.row[key].length === 0) {
      // No FK ID set — use cached name if available (e.g. FK was deleted after lock)
      const fallback = fallbackFn?.(props.row);
      if (fallback) {
        return <>{fallback}</>;
      }
      return <></>;
    }

    if (multiple) {
      // Always prefer the DB-cached names (populated on save and kept in
      // sync by the store's setValue). This ensures renamed / deleted
      // master-data entries never bleed into existing rows — locked or
      // unlocked. Falls back to a live-map lookup only when no cache is
      // available (e.g. legacy rows that predate the name-cache fields).
      if (fallbackFn) {
        const fallback = fallbackFn(props.row);
        if (fallback) return <>{fallback}</>;
      }

      const values = props.row[key].split(",") as string[];
      return (
        <>
          {values
            .map((value) => _format(map.get(parseInt(value, 10))))
            .join(", ")}
        </>
      );
    }

    // Always prefer the DB-cached name (populated on save and kept in sync by
    // the store's setValue). This ensures renamed master-data entries never bleed
    // into existing rows — locked or unlocked.
    if (fallbackFn) {
      const fallback = fallbackFn(props.row);
      if (fallback) return <>{fallback}</>;
    }

    // Fallback: no cached name yet (e.g. old record) — resolve from live assets map.
    const mapped = map.get(parseInt(props.row[key] as string, 10));
    return <>{_format(mapped)}</>;
  });
}

export default valueMapMapper;
